// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import ComparisonImage from "../../ExamDetailsScreen/ComparisonImage/ComparisonImage";
import LegacyVFCompactSummary from "../../ExamDetailsScreen/ComparisonImage/LegacyVFExam/CompactSummary/LegacyVFCompactSummary";
import { CloseIcon, DownloadIcon, DragIcon, InfoIcon } from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getSelectedCardHeaderBackground,
} from "src/styles/mui/muiStylesHelper";

// Types
import { ExternalExamType } from "src/services/exams/types";

// Utils
import { getLocalDate, getStartOfToday } from "src/utils/date/dateHelper";
import {
  downloadExternalExamFile,
  resolveExternalExamViewer,
} from "src/utils/exams/externalExamViewerHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Constants
import { eyeTypeEnums, OCT_TYPE, VISUAL_FIELD_TYPE } from "src/constants/enums";

// Hooks
import { useExternalExamById } from "src/services/exams/hooks/external-exams/useExternalExamById";
import Tooltip from "@mui/material/Tooltip";

type ComparisonCardType = {
  selectedExam: ExternalExamType;
  isDragMode: boolean;
  isViewDetailsDisabled: boolean;
  // Used by the "grouped" grid layout to add extra space after the last
  // card of each block, so a block stays close together but is visually
  // separated from the next one.
  marginBottom?: string;
  // Used by asymmetric layouts to size this card's image area to its
  // pane's designated height instead of the default 300px -- e.g. a
  // "small" pane in a 1/3-2/3 split. A value of "100%" instead makes the
  // image area fill whatever height the card is stretched to by its grid
  // cell, for cards that span multiple grid rows.
  imageHeight?: string;
  // Used by "spanning" layouts to explicitly place this card in the grid
  // (e.g. spanning multiple columns/rows), instead of relying on normal
  // grid auto-flow.
  gridColumn?: string;
  gridRow?: string;
  onDeselect: () => void;
  onViewDetails: () => void;
};

const ComparisonCard = ({
  selectedExam,
  isDragMode,
  isViewDetailsDisabled,
  marginBottom,
  imageHeight,
  gridColumn,
  gridRow,
  onDeselect,
  onViewDetails,
}: ComparisonCardType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const selectedHeaderBackground = getSelectedCardHeaderBackground(theme);
  const { clinicInfo } = useClinicContext();
  const { selectedPatient } = useExternalExamsContext();

  const { data: externalExamData } = useExternalExamById(clinicInfo?.id, {
    patientId: selectedPatient?.id,
    examId: selectedExam?.id,
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id: selectedExam.id });

  const dragStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    marginBottom,
    gridColumn,
    gridRow,
  };

  // Reload PDF iframe on browser resize
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Check rendered card width to determine if the PDF display needs zooming in
  const [cardWidth, setCardWidth] = useState(0);

  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node) return;

    let lastWidth = node.getBoundingClientRect().width;
    let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

    setCardWidth(lastWidth);

    const observer = new ResizeObserver(([entry]) => {
      const newWidth = entry.contentRect.width;
      setCardWidth(newWidth);

      if (Math.abs(newWidth - lastWidth) < 4) return;

      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        lastWidth = newWidth;
        setReloadKey((prev) => prev + 1);
      }, 400);
    });

    observer.observe(node);

    return () => {
      clearTimeout(debounceTimeout);
      observer.disconnect();
    };
  }, []);

  const isSmOrSmaller =
    cardWidth > 0 && cardWidth < theme.breakpoints.values.sm;

  const completionDate = useMemo(
    () =>
      getLocalDate(
        selectedExam?.date ?? getStartOfToday(clinicInfo?.timezone),
        clinicInfo?.timezone
      ),
    [selectedExam?.date, clinicInfo?.timezone]
  );

  const isVisualField = useMemo(
    () => selectedExam?.examType?.includes(VISUAL_FIELD_TYPE),
    [selectedExam?.examType]
  );

  const cardTitle = useMemo(() => {
    if (selectedExam?.examType?.includes(VISUAL_FIELD_TYPE)) {
      return t("Visual Field");
    } else if (selectedExam?.examType?.includes(OCT_TYPE)) {
      return t("OCT");
    } else {
      return t("OPD");
    }
  }, [selectedExam?.examType, t]);

  const eyeLabel = useMemo(() => {
    if (externalExamData?.eye === eyeTypeEnums.LEFT) return "OS";
    if (externalExamData?.eye === eyeTypeEnums.RIGHT) return "OD";
    return "OU";
  }, [externalExamData?.eye]);

  const handleDownload = useCallback(() => {
    if (!externalExamData?.examFileUrl) return;
    downloadExternalExamFile(
      externalExamData.examFileUrl,
      `${cardTitle}-${completionDate}`
    );
  }, [externalExamData?.examFileUrl, cardTitle, completionDate]);

  const infoTooltipContent = useMemo(
    () => (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography fontSize="12px">
          {cardTitle} ({eyeLabel})
        </Typography>
        <Typography fontSize="12px">{completionDate}</Typography>
        <Typography fontSize="12px">{selectedExam.sourcePlatform}</Typography>
        {isVisualField && (
          <>
            <Typography fontSize="12px">
              {t("MD")}: {externalExamData?.data?.meanDeviation ?? "N/A"}
            </Typography>
            <Typography fontSize="12px">
              {t("PSD")}:{" "}
              {externalExamData?.data?.patternStandardDeviation ?? "N/A"}
            </Typography>
          </>
        )}
      </Box>
    ),
    [
      t,
      cardTitle,
      eyeLabel,
      completionDate,
      selectedExam.sourcePlatform,
      isVisualField,
      externalExamData?.data?.meanDeviation,
      externalExamData?.data?.patternStandardDeviation,
    ]
  );

  return (
    <Box
      id={selectedExam.id}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "4px",
        border: "1px solid #E2772E",
        overflow: "hidden",
        minHeight: 0,
        minWidth: 0,
        zIndex: isDragMode ? 10 : 1,
      }}
      style={{ ...dragStyle }}
      ref={setNodeRef}
    >
      <Box
        ref={setActivatorNodeRef}
        onClick={isViewDetailsDisabled ? undefined : onViewDetails}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          p: "8px 12px",
          backgroundColor: selectedHeaderBackground,
          color: onSurface,
          borderBottom: "1px solid #E2772E",
          cursor: isViewDetailsDisabled
            ? "default"
            : isDragMode
              ? "grabbing"
              : "grab",
        }}
        {...attributes}
        {...listeners}
      >
        <DragIcon
          width="16px"
          height="16px"
          style={{
            flexShrink: 0,
            cursor: isDragMode ? "grabbing" : "grab",
          }}
        />
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography fontSize="14px" fontWeight={600} noWrap>
            {eyeLabel} · {cardTitle}
          </Typography>
          <Typography fontSize="12px" noWrap>
            {selectedExam?.sourcePlatform}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <Typography
            fontSize="12px"
            sx={{ display: { xs: "none", sm: "flex" } }}
          >
            {eyeLabel}
          </Typography>
          {externalExamData?.examFileUrl && (
            <IconButton
              disableRipple
              disableFocusRipple
              aria-label={t("Download exam file")}
              onClick={(event) => {
                event.stopPropagation();
                handleDownload();
              }}
              sx={{ color: "#E2772E", padding: "2px" }}
            >
              <DownloadIcon width="16px" height="16px" />
            </IconButton>
          )}
          <Tooltip title={infoTooltipContent}>
            <IconButton
              disableRipple
              disableFocusRipple
              onClick={(event) => event.stopPropagation()}
              sx={{ color: "#E2772E", padding: "2px" }}
            >
              <InfoIcon width="16px" height="16px" />
            </IconButton>
          </Tooltip>
          <IconButton
            disableRipple
            disableFocusRipple
            onClick={(event) => {
              event.stopPropagation();
              onDeselect();
            }}
            sx={{ color: onSurface, padding: "2px" }}
          >
            <CloseIcon width="16px" height="16px" />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={imageContainerRef}
        sx={{
          // A spanning card's own root Box stretches to fill its (taller)
          // grid cell by default -- fill that with flex instead of a fixed
          // height so the image area actually uses the extra space rather
          // than leaving it blank below a 300px image.
          ...(imageHeight === "100%"
            ? { flex: 1, minHeight: 0 }
            : { height: imageHeight ?? "300px" }),
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#FFF",
          p: externalExamData ? 0 : "16px",
        }}
      >
        {externalExamData &&
        resolveExternalExamViewer(externalExamData) !== "none" ? (
          <ComparisonImage
            key={reloadKey}
            selectedExam={externalExamData}
            hideToolbar
            octOverviewWidth="25%"
            octFillOverviewHeight
            octCompactSlider
            zoomIframe={isSmOrSmaller}
            showZoomControls
          />
        ) : selectedExam?.examThumbnailUrl ? (
          <img
            src={selectedExam.examThumbnailUrl}
            alt="external-exam-img"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        ) : isVisualField && externalExamData?.data?.algorithmType ? (
          <LegacyVFCompactSummary
            patient={selectedPatient}
            clinic={clinicInfo}
            externalExam={externalExamData}
          />
        ) : (
          <Typography
            sx={{
              color: "#000",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {t("No image preview available.")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ComparisonCard;
